import Link from "next/link";
import Image from "next/image";
import { useState } from "react";
import Slider from "react-slick";
import Loader from "../../Loader";
import useSWRImmutable from "swr/immutable";
import { NextArrow, PrevArrow } from "../../ArrowCarousel";
import { ConvertDateToTimeSince } from "../../../helpers/util";
import YoutubeIcon from "../../../../public/images/icons/youtube-multimedia.svg";
import styles from "../../../styles/multimedia/Multimedia.module.scss";

const cfUrl = process.env.API_CF_URL;
const Youtube = () => {
  const channels = {
    askYourElectionLawyer: "PLxIGRNqt1BBggGlH8noWCKJL-IqrDvQ49",
    inTheRunning: "PLxIGRNqt1BBhfdosys00bRZquYYihTyGz",
    onTheCampaignTrail: "PLxIGRNqt1BBgL7p89TJYnlFJuAXmS73xS",
    bosesNgKalye: "PLxIGRNqt1BBh1vwaRoP7vCaiXcR6IapmC",
    firstHundredDays: "PLxIGRNqt1BBhOf_FboZfxQ0xL460B6swF",
    explainers: "PLxIGRNqt1BBgTB9edIkjcU1r7zxcA0V4S",
    reports: "PLxIGRNqt1BBigaHQIPXT_q6jo650kYp0N",
  };

  const [activeVideo, setActiveVideo] = useState({});
  const [currentChannel, setCurrentChannel] = useState(
    channels.firstHundredDays
  );

  const RenderYoutubeFeed = ({ playlistId }) => {
    // const fetcher = (...args) => fetch(...args).then((res) => res.json());
    const fetcher = (url, body) =>
      fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body,
      }).then(async (res) => {
        return res.json();
      });

    const { data: rawData, error } = useSWRImmutable(
      [
        `${cfUrl}/getRSS`,
        JSON.stringify({
          data: {
            type: "yt",
            multiple: playlistId === "all",
            playlistId:
              playlistId === "all"
                ? Object.values(channels).join()
                : playlistId,
          },
        }),
      ],
      fetcher,
      {
        refreshInterval: 0,
        shouldRetryOnError: true,
        errorRetryInterval: 1500,
        errorRetryCount: 1,
      }
    );
    const data = rawData?.result;

    if (!error && !data) return <Loader />;
    if (error) return <p>Something went wrong, Please try again later!</p>;

    return data.map((youtubeData, i) => (
      <div key={i} className={styles.videoContainer}>
        {!activeVideo[i] && (
          <div
            className={styles.videoOverlay}
            onClick={() => {
              handleCustomPlayClick(i);
            }}
          >
            <div className={styles.imageWrap}>
              <Image
                src={`https://i2.ytimg.com/vi/${youtubeData.ytVideoId}/maxresdefault.jpg`}
                alt={youtubeData.title}
                layout="fill"
                objectFit="cover"
              />
            </div>
            <button className={styles.customButton} />
          </div>
        )}
        {!!activeVideo[i] && (
          <iframe
            src={`https://www.youtube.com/embed/${youtubeData.ytVideoId}?controls=0&autoplay=1`}
            title={youtubeData.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          />
        )}
        <div className={styles.youtubeDesc}>
          <h3>{youtubeData.title}</h3>
          {youtubeData.title.length +
            youtubeData.media["media:description"].toString().length >
          180 ? (
            <p className={styles.description}>
              {youtubeData.media["media:description"].toString().slice(0, 180) +
                "..."}
            </p>
          ) : (
            <p className={styles.description}>
              {youtubeData.media["media:description"].toString()}
            </p>
          )}
          <p className={styles.pubDate}>
            {ConvertDateToTimeSince(youtubeData.pubDate)}
          </p>
        </div>
      </div>
    ));
  };

  const loader = ({ src, width, quality }) => {
    return `${src}?w=${width}&q=${quality || 75}`;
  };

  const YoutubeContent = () => {
    switch (currentChannel) {
      case channels.askYourElectionLawyer:
        return (
          <>
            <h2 className={styles.channelTitle}>Ask Your Election Lawyer</h2>
            <div className={styles.descriptionWrapper}>
              <p className={styles.description}>
                We answer your questions about laws, rules, and practical
                concerns about campaigning, voting, and canvassing.
              </p>
              <div className={styles.iconContainer}>
                <h6 className={styles.smallHeadline}>Watch us on</h6>
                <div className={styles.iconWrapper}>
                  <div className={styles.channelIcon}>
                    <Link
                      href="https://youtube.com/playlist?list=PLxIGRNqt1BBggGlH8noWCKJL-IqrDvQ49"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={YoutubeIcon}
                          alt="Youtube"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      case channels.inTheRunning:
        return (
          <>
            <h2 className={styles.channelTitle}>In The Running</h2>
            <div className={styles.descriptionWrapper}>
              <p className={styles.description}>
                Join Rappler&apos;s reporters and editors as they delve into the
                candidates’ road to the presidency and other elective positions.
              </p>
              <div className={styles.iconContainer}>
                <h6 className={styles.smallHeadline}>Watch us on</h6>
                <div className={styles.iconWrapper}>
                  <div className={styles.channelIcon}>
                    <Link
                      href="https://youtube.com/playlist?list=PLxIGRNqt1BBhfdosys00bRZquYYihTyGz"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={YoutubeIcon}
                          alt="Youtube"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      case channels.onTheCampaignTrail:
        return (
          <>
            <h2 className={styles.channelTitle}>On The Campaign Trail </h2>
            <div className={styles.descriptionWrapper}>
              <p className={styles.description}>
                Hosted by veteran journalist John Nery, the show focuses on key
                issues, moments, and personalities in the long run-up to
                Election Day. But we want to be clear about the lens we will
                use: We know that the democratic project is in great danger, and
                that shapes how we see the 2022 vote. It isn’t just elections as
                usual.
              </p>
              <div className={styles.iconContainer}>
                <h6 className={styles.smallHeadline}>Watch us on</h6>
                <div className={styles.iconWrapper}>
                  <div className={styles.channelIcon}>
                    <Link
                      href="https://youtube.com/playlist?list=PLxIGRNqt1BBgL7p89TJYnlFJuAXmS73xS "
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={YoutubeIcon}
                          alt="Youtube"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      case channels.bosesNgKalye:
        return (
          <>
            <h2 className={styles.channelTitle}>Boses Ng Kalye</h2>
            <div className={styles.descriptionWrapper}>
              <p className={styles.description}>
                This is a video series that seeks to get a random pulse of
                sentiments on the ground during a particular period of time and
                in a particular place. It is by no means indicative of
                scientific trends among potential voters.
              </p>
              <div className={styles.iconContainer}>
                <h6 className={styles.smallHeadline}>Watch us on</h6>
                <div className={styles.iconWrapper}>
                  <div className={styles.channelIcon}>
                    <Link
                      href="https://youtube.com/playlist?list=PLxIGRNqt1BBh1vwaRoP7vCaiXcR6IapmC"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={YoutubeIcon}
                          alt="Youtube"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      case channels.firstHundredDays:
        return (
          <>
            <h2 className={styles.channelTitle}>First 100 Days</h2>
            <div className={styles.descriptionWrapper}>
              <p className={styles.description}>
                Nobel Peace Prize laureate and Rappler CEO Maria Ressa sits down
                with four presidential candidates – Manny Pacquiao, Isko Moreno,
                Leni Robredo, and Leody de Guzman – and asks them this question:
                What will you do on your first 100 days in office, if you become
                president?
              </p>
              <div className={styles.iconContainer}>
                <h6 className={styles.smallHeadline}>Watch us on</h6>
                <div className={styles.iconWrapper}>
                  <div className={styles.channelIcon}>
                    <Link
                      href="https://youtube.com/playlist?list=PLxIGRNqt1BBhOf_FboZfxQ0xL460B6swF"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={YoutubeIcon}
                          alt="Youtube"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      case channels.explainers:
        return (
          <>
            <h2 className={styles.channelTitle}>Explainers</h2>
            <div className={styles.descriptionWrapper}>
              <p className={styles.description}>
                Rappler’s election explainers break down must-know
                election-related concepts, ideas, and in-depth stories.
              </p>
              <div className={styles.iconContainer}>
                <h6 className={styles.smallHeadline}>Watch us on</h6>
                <div className={styles.iconWrapper}>
                  <div className={styles.channelIcon}>
                    <Link
                      href="https://youtube.com/playlist?list=PLxIGRNqt1BBgTB9edIkjcU1r7zxcA0V4S"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={YoutubeIcon}
                          alt="Youtube"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      case channels.reports:
        return (
          <>
            <h2 className={styles.channelTitle}>Reports</h2>
            <div className={styles.descriptionWrapper}>
              <p className={styles.description}>
                Watch Rappler’s campaign reporters deliver recaps and updates on
                the top presidential and vice presidential candidates in the
                2022 Philippine Elections.
              </p>
              <div className={styles.iconContainer}>
                <h6 className={styles.smallHeadline}>Watch us on</h6>
                <div className={styles.iconWrapper}>
                  <div className={styles.channelIcon}>
                    <Link
                      href="https://youtube.com/playlist?list=PLxIGRNqt1BBigaHQIPXT_q6jo650kYp0N"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={YoutubeIcon}
                          alt="Youtube"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      default:
        return (
          <>
            <h2 className={styles.channelTitle}>ALL </h2>
            <div className={styles.descriptionWrapper}>
              <p className={styles.description}></p>
              <div className={styles.iconContainer}>
                <h6 className={styles.smallHeadline}>Watch us on</h6>
                <div className={styles.iconWrapper}>
                  <div className={styles.channelIcon}>
                    <Link
                      href="https://youtube.com/playlist?list=PLxIGRNqt1BBggGlH8noWCKJL-IqrDvQ49"
                      passHref
                    >
                      <a target="_blank">
                        <Image
                          loader={loader}
                          src={YoutubeIcon}
                          alt="Youtube"
                          layout="responsive"
                        />
                      </a>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
    }
  };

  const settings = {
    dots: false,
    arrows: true,
    infinite: true,
    focusOnSelect: true,
    centerMode: true,
    slidesToShow: 2.3,
    slidesToScroll: 1,
    adaptiveHeight: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          infinite: false,
          adaptiveHeight: false,
          slidesToShow: 1.5,
          slidesToScroll: 1.5,
        },
      },
    ],
  };

  const handleCustomPlayClick = (i) => {
    setActiveVideo({ ...activeVideo, [i]: !activeVideo[i] });
  };

  const activeChannel = (selectedChannel) =>
    currentChannel === selectedChannel ? styles.active : "";

  return (
    <section className={`${styles.multimediaWrapper} ${styles.youtube}`}>
      <h2 className={styles.sectionTitle}>Youtube</h2>
      <div className={styles.channelTabs}>
        <button
          className={activeChannel("all")}
          onClick={() => setCurrentChannel("all")}
        >
          All
        </button>
        <button
          className={activeChannel(channels.firstHundredDays)}
          onClick={() => setCurrentChannel(channels.firstHundredDays)}
        >
          First 100 days
        </button>
        <button
          className={activeChannel(channels.explainers)}
          onClick={() => setCurrentChannel(channels.explainers)}
        >
          Explainers
        </button>
        <button
          className={activeChannel(channels.reports)}
          onClick={() => setCurrentChannel(channels.reports)}
        >
          Reports
        </button>
        <button
          className={activeChannel(channels.askYourElectionLawyer)}
          onClick={() => setCurrentChannel(channels.askYourElectionLawyer)}
        >
          Ask Your Election Lawyer
        </button>
        <button
          className={activeChannel(channels.inTheRunning)}
          onClick={() => setCurrentChannel(channels.inTheRunning)}
        >
          In The Running
        </button>
        <button
          className={activeChannel(channels.onTheCampaignTrail)}
          onClick={() => setCurrentChannel(channels.onTheCampaignTrail)}
        >
          on The Campaign Trail
        </button>
        <button
          className={activeChannel(channels.bosesNgKalye)}
          onClick={() => setCurrentChannel(channels.bosesNgKalye)}
        >
          Boses Ng Kalye
        </button>
      </div>
      <div className={styles.channelsDescription}>{YoutubeContent()}</div>
      <Slider key={Date.now()} {...settings}>
        {RenderYoutubeFeed({ playlistId: currentChannel })}
      </Slider>
    </section>
  );
};

export default Youtube;
